import { format, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import type {
  PurchaseProofPurchaseProofCollectionReadModel,
  PurchaseProofPurchaseProofCollectionReadModelStatus
} from '@r/api/generated/client.schemas.ts';
import { Badge } from '@ui/badge.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import { cn } from '@r/utils/utils.ts';

const statusStyles: Record<
  PurchaseProofPurchaseProofCollectionReadModelStatus,
  { dotClass: string }
> = {
  pending: { dotClass: 'bg-amber-500' },
  processing: { dotClass: 'bg-blue-500' },
  approved: { dotClass: 'bg-emerald-600' },
  rejected: { dotClass: 'bg-red-500' }
};

/** Formats a deposit date for display in French locale. */
function formatDepositDate(createdAt: string) {
  const date = new Date(createdAt);
  return isValid(date) ? format(date, 'dd/MM/yyyy', { locale: fr }) : createdAt;
}

/** Displays a truncated comment and exposes its full content in a tooltip. */
function CommentCell({ comment }: { comment?: string | null }) {
  const { t } = useTranslation();
  if (!comment) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="block max-w-64 cursor-help truncate text-left"
            aria-label={t('purchase.proof.comment.show')}
          >
            {comment}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm whitespace-normal break-words">
          {comment}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Displays the translated purchase-proof status with its visual indicator. */
function StatusBadge({ status }: { status: PurchaseProofPurchaseProofCollectionReadModelStatus }) {
  const { t } = useTranslation();
  const style = statusStyles[status];

  return (
    <Badge variant="outline" className="gap-1.5 border-muted-foreground/20 font-normal">
      <span className={cn('size-1.5 shrink-0 rounded-full', style.dotClass)} aria-hidden />
      {t(`purchase.proof.status.${status}`)}
    </Badge>
  );
}

/** Returns the translated column configuration for the user's deposit history. */
export function usePurchaseProofHistoryColumns(): ColumnDef<PurchaseProofPurchaseProofCollectionReadModel>[] {
  const { t } = useTranslation();

  return [
    {
      accessorKey: 'filename',
      header: t('invoices.history.filename'),
      enableSorting: false,
      enableHiding: false
    },
    {
      accessorKey: 'comment',
      header: t('invoices.history.comment'),
      enableSorting: false,
      cell: ({ row }) => <CommentCell comment={row.original.comment} />
    },
    {
      accessorKey: 'status',
      header: t('invoices.history.status'),
      enableSorting: false,
      cell: ({ row }) => <StatusBadge status={row.original.status} />
    },
    {
      accessorKey: 'createdAt',
      header: t('invoices.history.date'),
      enableSorting: false,
      cell: ({ row }) => formatDepositDate(row.original.createdAt)
    }
  ];
}
